import { Component, Vue } from 'vue-property-decorator';
import { EVALUATION_STATUSES, PAGE_NAMES } from '@/constants';
import { convertNumberToFourDigitsString, formatDateToDisplay, sortArrayByDateField } from '@/helpers';
import { AssessmentPeriodDTO, DisplayStatus, ProjectDTO, TableHeaderColumns } from '@/models';
import { fetchAssessmentPeriods, fetchAverageSatAndComplexity, fetchProjectsOfCurrentUser } from '@/services';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import PageLoader from '@/components/common/loader/page-loader/PageLoader.vue';
import ProjectExporting from '@/services/project-exporting';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import Status from '@/components/common/status/Status.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';

type ProjectInTable = Omit<ProjectDTO, 'id'> & { id: string };

@Component({
  components: {
    InlineLoader,
    PageLoader,
    SearchInput,
    Status,
    Pagination
  }
})
export default class ProjectExport extends Vue {
  private projects: ProjectDTO[] = [];
  private assessmentPeriods: AssessmentPeriodDTO[] = [];
  private isInitialDataFetching = true;
  private isExporting = false;
  private headers: TableHeaderColumns = [
    { text: 'Project ID', value: 'id', align: 'start' },
    { text: 'Project name', value: 'name' },
    { text: 'Evaluation status', value: 'projectStatus' },
    { text: 'Created at', value: 'createdAt' }
  ];
  private selected: ProjectDTO[] = [];
  private search = '';
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;

  get hasProjects(): boolean {
    return !!this.projects.length;
  }

  get displayTableData(): ProjectInTable[] {
    return this.projects.map(project => ({
      ...project,
      id: convertNumberToFourDigitsString(project.id)
    }));
  }

  filterProjects = (projects: ProjectDTO[]): ProjectDTO[] => {
    const { isAdmin, id: userId } = this.$store.getters['app/currentUser'];
    if (isAdmin) {
      return projects;
    }
    return projects.filter(project => project.sponsor.id === userId || project.manager.id === userId);
  };

  onSearch(searchText: string): void {
    this.search = searchText;
  }

  getDisplayStatus(status: string): DisplayStatus {
    return EVALUATION_STATUSES[status];
  }

  getDisplayID(id: number): string {
    return convertNumberToFourDigitsString(id);
  }

  getDisplayDate(date: string): string {
    return formatDateToDisplay(date);
  }

  navigateToProjectListPage(): void {
    this.$router.push({ name: PAGE_NAMES.PROJECT_LIST });
  }

  async exportMultipleProjects(): Promise<void> {
    this.isExporting = true;
    const { data } = await fetchAverageSatAndComplexity();
    const projectExporting = new ProjectExporting(this.assessmentPeriods, data);
    await projectExporting.exportXLSXMultipleProjects(this.selected);
    this.isExporting = false;
  }

  async created(): Promise<void> {
    const { data: projects } = await fetchProjectsOfCurrentUser();
    const { data: assessmentPeriods } = await fetchAssessmentPeriods();
    this.projects = sortArrayByDateField(this.filterProjects(projects || []), 'createdAt');
    this.assessmentPeriods = assessmentPeriods || [];
    this.isInitialDataFetching = false;
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }
}
